import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { SafeImage } from './SafeImage';

// Cached module-level so subsequent renders don't re-fetch
let _cachedSettings = null;
let _pending = null;

async function loadSettings() {
    if (_cachedSettings) return _cachedSettings;
    if (_pending) return _pending;
    _pending = api.get('/business-settings/public')
        .then(r => { _cachedSettings = r.data || {}; return _cachedSettings; })
        .catch(() => ({}))
        .finally(() => { _pending = null; });
    return _pending;
}

let _faviconApplied = false;
function applyFavicon(url) {
    if (!url || _faviconApplied || typeof document === 'undefined') return;
    _faviconApplied = true;
    let link = document.querySelector('link[rel~="icon"]');
    if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
    }
    link.href = url;
}
function applyTitle(name) {
    if (!name || typeof document === 'undefined') return;
    const suffix = 'Fresh Plants, Pots & Garden Essentials';
    document.title = `${name} | ${suffix}`;
}

/**
 * Logo component.
 *
 * When `business_settings.logo_url` is set, we render the image as the full
 * brand mark and DO NOT emit the fallback wordmark text (because the uploaded
 * logo image usually already contains the wordmark). If no image is set we
 * fall back to an SVG monogram + wordmark rendered separately.
 *
 * Props:
 *   size       - primary sizing token in px (height of the mark)
 *   showText   - if false, force-hide the text wordmark even in fallback mode
 *   textColor  - color for fallback wordmark text
 *   variant    - 'auto' | 'mark-only' | 'full'
 *                  auto: uses image if available, else mark + text
 *                  mark-only: only the icon/mark box (good for tight UIs)
 *                  full: image if avail, text always shown alongside
 */
export function Logo({ size = 40, showText = true, textColor = 'white', variant = 'auto' }) {
    const [logoUrl, setLogoUrl] = useState(null);
    const [name, setName] = useState('DHARMIK');
    const [tagline, setTagline] = useState('GARDENS');
    const [fullName, setFullName] = useState('Dharmik Gardens');

    useEffect(() => {
        let mounted = true;
        loadSettings().then(s => {
            if (!mounted) return;
            if (s?.logo_url) { setLogoUrl(s.logo_url); applyFavicon(s.favicon_url || s.logo_url); }
            if (s?.name) {
                setFullName(s.name);
                applyTitle(s.name);
                const parts = s.name.trim().toUpperCase().split(/\s+/);
                if (parts.length >= 2) {
                    setName(parts[0]);
                    setTagline(parts.slice(1).join(' '));
                } else {
                    setName(parts[0] || 'DHARMIK');
                }
            }
        });
        return () => { mounted = false; };
    }, []);

    const hasImage = !!logoUrl;
    // When we have an image, render it wider (aspect ≈ 3:1 works for horizontal wordmark logos)
    // and skip the separately-rendered text (variant='auto').
    if (hasImage && variant !== 'full') {
        return (
            <div className="inline-flex items-center" data-testid="app-logo" title={fullName}>
                <SafeImage
                    src={logoUrl}
                    alt={fullName}
                    className="object-contain"
                    style={{ height: size, width: 'auto', maxWidth: size * 4 }}
                    iconClassName="h-5 w-5"
                />
                {variant === 'mark-only' && null}
            </div>
        );
    }

    // Fallback: monogram + wordmark
    return (
        <div className="flex items-center gap-2.5" data-testid="app-logo">
            <div
                className="relative flex items-center justify-center rounded-md overflow-hidden"
                style={{ width: size, height: size, background: 'hsl(226 76% 27%)' }}
            >
                {hasImage ? (
                    <SafeImage src={logoUrl} alt={fullName} className="h-full w-full object-contain" iconClassName="h-5 w-5" />
                ) : (
                    <svg viewBox="0 0 60 60" width={size * 0.7} height={size * 0.7}>
                        <path
                            d="M30 6 C15 6 6 18 6 30 C6 42 15 54 30 54 C39 54 46 49 50 42 C42 44 34 40 34 30 C34 22 40 16 48 18 C44 10 37 6 30 6 Z"
                            fill="hsl(142 76% 58%)"
                        />
                    </svg>
                )}
            </div>
            {showText && variant !== 'mark-only' && (
                <div className="leading-tight" style={{ color: textColor }}>
                    <div className="font-extrabold text-sm sm:text-base tracking-tight">{name}</div>
                    <div className="text-[10px] sm:text-xs font-medium opacity-90">{tagline}</div>
                </div>
            )}
        </div>
    );
}

// Expose a helper for consumers who want to force re-fetch after admin saves
export function invalidateLogoCache() {
    _cachedSettings = null;
    _faviconApplied = false;
}

export default Logo;
